import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { managedDeviceGroupsApi } from '../services/managedDeviceGroupsApi'
import { getTenantId } from '../config/tenant'

// Main hook for fetching managed device groups.
// typeId recorta pela aba (tipo de device); omitido = todas as abas (legado).
export const useManagedDeviceGroups = (parentGroupId = null, typeId = null) => {
  const tenantId = getTenantId()

  return useQuery({
    queryKey: ['managedDeviceGroups', tenantId, parentGroupId, typeId],
    queryFn: () => managedDeviceGroupsApi.getByLevel(tenantId, parentGroupId, typeId),
    enabled: !!tenantId,
    staleTime: 60000, // Cache for 1 minute
  })
}

// Hook for fetching the group tree (typeId recorta pela aba)
export const useManagedDeviceGroupTree = (typeId = null) => {
  const tenantId = getTenantId()

  return useQuery({
    queryKey: ['managedDeviceGroups', 'tree', tenantId, typeId],
    queryFn: () => managedDeviceGroupsApi.getTree(tenantId, typeId),
    enabled: !!tenantId,
    staleTime: 60000, // Cache for 1 minute
  })
}

// Hook for fetching single group
export const useManagedDeviceGroup = (id) => {
  return useQuery({
    queryKey: ['managedDeviceGroup', id],
    queryFn: () => managedDeviceGroupsApi.getById(id),
    enabled: !!id,
    staleTime: 30000, // Cache for 30 seconds
  })
}

// Mutation hooks for group management
export const useCreateManagedDeviceGroup = () => {
  const tenantId = getTenantId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data) => managedDeviceGroupsApi.create(tenantId, data),
    onSuccess: () => {
      // Invalidate all group queries to ensure tree updates
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroups'] })
    },
  })
}

export const useUpdateManagedDeviceGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => managedDeviceGroupsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroup', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroups'] })
    },
  })
}

export const useDeleteManagedDeviceGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => managedDeviceGroupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroups'] })
    },
  })
}

export const useMoveManagedDeviceGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, parentGroupId }) => managedDeviceGroupsApi.moveGroup(id, parentGroupId),
    onSuccess: () => {
      // Moving groups can affect the entire tree structure
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroups'] })
    },
  })
}

// Convenience hooks for root and child groups
export const useRootManagedDeviceGroups = () => {
  return useManagedDeviceGroups(null)
}

export const useChildManagedDeviceGroups = (parentGroupId) => {
  return useManagedDeviceGroups(parentGroupId)
}

// Backward compatibility hooks for router groups
export const useRouterGroups = (parentGroupId = null) => {
  return useManagedDeviceGroups(parentGroupId)
}

export const useRouterGroupTree = () => {
  return useManagedDeviceGroupTree()
}

export const useRouterGroup = (id) => {
  return useManagedDeviceGroup(id)
}

export const useCreateRouterGroup = useCreateManagedDeviceGroup
export const useUpdateRouterGroup = useUpdateManagedDeviceGroup
export const useDeleteRouterGroup = useDeleteManagedDeviceGroup
export const useMoveRouterGroup = useMoveManagedDeviceGroup

// Backward compatibility hooks for host groups
export const useHostGroups = (parentGroupId = null) => {
  return useManagedDeviceGroups(parentGroupId)
}

export const useHostGroupTree = () => {
  return useManagedDeviceGroupTree()
}

export const useHostGroup = (id) => {
  return useManagedDeviceGroup(id)
}

export const useCreateHostGroup = useCreateManagedDeviceGroup
export const useUpdateHostGroup = useUpdateManagedDeviceGroup
export const useDeleteHostGroup = useDeleteManagedDeviceGroup
export const useMoveHostGroup = useMoveManagedDeviceGroup